/**
 * ChecklistAddItemRow - "Add an item on the fly" control for a live checklist.
 *
 * Field crews routinely do work the template never anticipated — an extra
 * stairwell on an inspection, a block of rooms cleaned on a work order — and
 * need it on the record BEFORE the customer signs. This row is how they put it
 * there. The item lands on THIS job's checklist only; the source template in
 * the CMS is untouched.
 *
 * Collapsed to a single dashed button until used, so it never competes with
 * the rating buttons / checkboxes above it for attention. Once open it stays
 * open after each save — someone adding one room usually adds several — and
 * refocuses the input so the next title is one keystroke away, not one tap.
 *
 * Extracted from InspectionWorkflowChecklist when the same affordance was
 * added to the work order workflow. Both surfaces render the identical
 * control; only the placeholder differs, since "East stairwell" and
 * "Rooms 201-220" are the shapes of what each crew types.
 *
 * Keyboard: Enter saves, Escape closes and discards the draft. Both matter on
 * a tablet with a hardware keyboard, which is what the office-side users have.
 */
import React, { useEffect, useRef, useState } from 'react';

/* ============================================
 * TYPE DEFINITIONS
 * ============================================ */

export interface ChecklistAddItemRowProps {
	/**
	 * Persists a new item with the given title. Awaited — the row shows an
	 * "Adding…" state until it settles, so a slow connection can't produce
	 * two items from two impatient taps.
	 */
	onAddItem: (title: string) => Promise<void>;
	/** Placeholder inside the title input — phrase it as a real example. */
	placeholder?: string;
	/** Label on the collapsed button and the expanded save button. */
	addLabel?: string;
	/** BEM block prefix for the wrapper, so each host keeps its own class namespace. */
	block?: string;
	className?: string;
}

/* ============================================
 * COMPONENT
 * ============================================ */

export const ChecklistAddItemRow: React.FC<ChecklistAddItemRowProps> = ({
	onAddItem,
	placeholder = 'e.g. East stairwell',
	addLabel = 'Add item',
	block = 'checklist-add-item-row',
	className = '',
}) => {
	/* Draft state. `isOpen` gates the whole expanded form; `isSaving` blocks
	   a second submit while the first is still in flight. */
	const [isOpen, setIsOpen] = useState(false);
	const [title, setTitle] = useState('');
	const [isSaving, setIsSaving] = useState(false);
	const inputRef = useRef<HTMLInputElement>(null);

	/* Focus the input as soon as the row opens — on a phone this is the
	   difference between one tap and two. */
	useEffect(() => {
		if (isOpen) inputRef.current?.focus();
	}, [isOpen]);

	/** Close and discard whatever was half-typed. */
	const close = () => {
		setIsOpen(false);
		setTitle('');
	};

	const handleAdd = async () => {
		const trimmed = title.trim();
		if (!trimmed || isSaving) return;
		setIsSaving(true);
		try {
			await onAddItem(trimmed);
			setTitle('');
			/* Stay open — one addition is rarely the only one. */
			inputRef.current?.focus();
		} finally {
			setIsSaving(false);
		}
	};

	/* The input id is derived from the block so two of these rows on one
	   page can never collide on a label's htmlFor. */
	const inputId = `${block}__title`;

	return (
		<div className={[`${block}__add`, 'mt-8px', className].filter(Boolean).join(' ')} data-ref="molecule-checklist-add-item-row">
			{isOpen ? (
				<div className="flex flex-col gap-8px p-12px rounded-lg border border-dashed border-brand-border bg-white">
					<label htmlFor={inputId} className="text-12px font-sans font-medium text-brand-text-muted uppercase tracking-wide">
						New item
					</label>
					<input
						ref={inputRef}
						id={inputId}
						type="text"
						value={title}
						onChange={(e) => setTitle(e.target.value)}
						onKeyDown={(e) => {
							if (e.key === 'Enter') { e.preventDefault(); handleAdd(); }
							if (e.key === 'Escape') { close(); }
						}}
						placeholder={placeholder}
						className="w-full px-12px py-10px text-16px font-sans rounded-md border border-brand-border focus:outline-none focus:ring-2 focus:ring-brand-primary min-h-44px"
					/>
					<div className="flex items-center gap-8px">
						<button
							type="button"
							onClick={handleAdd}
							disabled={!title.trim() || isSaving}
							className="inline-flex items-center justify-center px-16px py-8px text-14px font-sans font-semibold text-white bg-brand-primary hover:bg-brand-primary-dark rounded-md min-h-44px transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
						>
							{isSaving ? 'Adding…' : addLabel}
						</button>
						<button
							type="button"
							onClick={close}
							className="inline-flex items-center justify-center px-12px py-8px text-14px font-sans text-brand-text-muted hover:text-brand-text min-h-44px transition-colors"
						>
							Done
						</button>
					</div>
				</div>
			) : (
				<button
					type="button"
					onClick={() => setIsOpen(true)}
					className="w-full inline-flex items-center justify-center gap-6px px-16px py-12px text-14px font-sans font-medium text-brand-primary hover:text-brand-primary-hover border border-dashed border-brand-border rounded-lg min-h-44px transition-colors"
				>
					<svg className="w-16px h-16px" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
						<path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
					</svg>
					{addLabel}
				</button>
			)}
		</div>
	);
};
